import {formatTimestamp, parseContent} from '@dv5/components'
import {DocumentPart, PartType} from '@dv5/domain'

export {getMarkdownFilename} from '@dv5/components'

const SECRET_PLACEHOLDER = '> 🔒 Secret part (content hidden)'
const MIN_FENCE_LENGTH = 3

const escapeTableCell = (value: string): string => value.replace(/\|/g, '\\|')

const parseCsvLine = (line: string): string[] => {
    const values: string[] = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
        const char = line[i]
        if (inQuotes) {
            if (char === '"') {
                if (i + 1 < line.length && line[i + 1] === '"') {
                    current += '"'
                    i++
                } else {
                    inQuotes = false
                }
            } else {
                current += char
            }
        } else if (char === '"') {
            inQuotes = true
        } else if (char === ',') {
            values.push(current)
            current = ''
        } else {
            current += char
        }
    }
    values.push(current)
    return values
}

const csvToMarkdownTable = (content: string): string => {
    const lines = content.split(/\r?\n/).filter(line => line.trim().length > 0)
    if (lines.length === 0) {
        return ''
    }
    const rows = lines.map(parseCsvLine)
    const columnCount = Math.max(...rows.map(row => row.length))
    const paddedRows = rows.map(row => {
        const padded = [...row]
        while (padded.length < columnCount) {
            padded.push('')
        }
        return padded
    })
    const [header, ...dataRows] = paddedRows
    const headerLine = `| ${header.map(escapeTableCell).join(' | ')} |`
    const separator = `| ${Array<string>(columnCount).fill('---').join(' | ')} |`
    const dataLines = dataRows.map(row => `| ${row.map(escapeTableCell).join(' | ')} |`)
    return [headerLine, separator, ...dataLines].join('\n')
}

const getFenceLength = (content: string): number => {
    const runs = content.match(/`+/g)
    if (!runs) {
        return MIN_FENCE_LENGTH
    }
    const longest = Math.max(...runs.map(run => run.length))
    return Math.max(MIN_FENCE_LENGTH, longest + 1)
}

const plainTextToMarkdown = (content: string): string => {
    const fence = '`'.repeat(getFenceLength(content))
    return `${fence}\n${content}\n${fence}`
}

const diaryToMarkdown = (content: string): string => {
    const {items, todos} = parseContent(content)
    if (items.length === 0 && todos.length === 0) {
        return '_No diary entries_'
    }
    const blocks: string[] = []
    if (items.length > 0) {
        const entryLines = items.map(item => {
            const endTime = item.startTime + item.duration
            return `- ${formatTimestamp(item.startTime)} - ${formatTimestamp(endTime)} — ${item.text}`
        })
        blocks.push(`### Entries\n\n${entryLines.join('\n')}`)
    }
    if (todos.length > 0) {
        const todoLines = todos.map(todo => `- [${todo.checked ? 'x' : ' '}] ${todo.title}`)
        blocks.push(`### Todos\n\n${todoLines.join('\n')}`)
    }
    return blocks.join('\n\n')
}

const partContentToMarkdown = (part: DocumentPart): string => {
    const content = part.partContent || ''
    switch (part.partType) {
        case PartType.MARKDOWN:
            return content
        case PartType.PLAINTEXT:
            return plainTextToMarkdown(content)
        case PartType.CSV_TABLE:
            return csvToMarkdownTable(content)
        case PartType.DIARY:
            return diaryToMarkdown(content)
        case PartType.SECRET:
            return SECRET_PLACEHOLDER
        default:
            return plainTextToMarkdown(content)
    }
}

export const exportDocumentToMarkdown = (
    title: string,
    description: string | null | undefined,
    parts: Array<DocumentPart>
): string => {
    const blocks: string[] = []
    if (title && title.length > 0) {
        blocks.push(`# ${title}`)
    }
    const trimmedDescription = (description || '').trim()
    if (trimmedDescription.length > 0) {
        blocks.push(trimmedDescription)
    }
    const sortedParts = [...parts].sort((a, b) => a.partIndex - b.partIndex)
    sortedParts.forEach(part => {
        const partBlocks: string[] = []
        if (part.partHeader && part.partHeader.length > 0) {
            partBlocks.push(`## ${part.partHeader}`)
        }
        const content = partContentToMarkdown(part)
        if (content.trim().length > 0) {
            partBlocks.push(content)
        }
        if (partBlocks.length > 0) {
            blocks.push(partBlocks.join('\n\n'))
        }
    })
    if (blocks.length === 0) {
        return ''
    }
    return blocks.join('\n\n') + '\n'
}
